class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    const atelier = this.closest(".bp-pdp--atelier");
    if (atelier) return this.initializeAtelier(atelier);

    const addToCartModule = document.querySelector(".wt-product__add-to-cart");
    const btn = this.querySelector("button");
    const addToCart = this.dataset.addToCart === "";

    const forObserver = document.querySelectorAll(
      ".wt-product__add-to-cart, .wt-footer, .wt-product__name",
    );

    this._bundleEl = document.querySelector(".wt-bundle");

    let intersected = [];

    const updateVisibility = () => {
      if (intersected.length || this._overlapsBundlePanel()) {
        this.classList.remove(this.activeClass);
      } else {
        this.classList.add(this.activeClass);
      }
    };

    btn.addEventListener("click", (e) => {
      if (!addToCart) {
        e.preventDefault();
        addToCartModule.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        const loader = this.querySelector(".loading-overlay__spinner");
        btn.classList.add("loading");
        if (loader) loader.classList.remove("hidden");
      }
    });

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      const loader = this.querySelector(".loading-overlay__spinner");
      btn.classList.remove("loading");
      if (loader) loader.classList.add("hidden");
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        isIntersecting
          ? intersected.push(target)
          : (intersected = intersected.filter((item) => item !== target));
      });

      updateVisibility();
    });

    forObserver.forEach((item) => {
      observer.observe(item);
    });

    if (this._bundleEl) {
      this._onScroll = updateVisibility;
      window.addEventListener("scroll", this._onScroll, { passive: true });
    }
  }


  // Atelier is an opt-in enhancement of this native element, not a second
  // product form or variant controller. All commercial state stays native.
  initializeAtelier(owner) {
    if (this._atelierState) return;
    if (!this._atelierLifecycle) {
      this._atelierLifecycle = (event) => {
        if (!(event.target instanceof Element) || !event.target.contains(this)) return;
        if (event.type === "shopify:section:unload") this.cleanupAtelier();
        else this.initialize();
      };
      document.addEventListener("shopify:section:load", this._atelierLifecycle);
      document.addEventListener("shopify:section:unload", this._atelierLifecycle);
    }

    const sectionId = owner.dataset.sectionId;
    const form = sectionId && owner.querySelector(
      `form[id="${CSS.escape("product-form-" + sectionId)}"]`,
    );
    const nativeButton = form?.querySelector('[name="add"][type="submit"]');
    const button = this.querySelector("button");
    if (!form || !nativeButton || !button) {
      this.classList.remove(this.activeClass);
      this.setAttribute("aria-hidden", "true");
      this.inert = true;
      return;
    }

    const state = { owner, form, nativeButton, button, unsubscribers: [] };
    this._atelierState = state;
    const directSubmit = () => this.dataset.addToCart === "" &&
      button.form === form &&
      form.closest("product-form")?.dataset.instantCheckout !== "true";
    const syncButton = () => {
      if (this._atelierState !== state) return;
      const loading = directSubmit() && nativeButton.classList.contains("loading");
      button.classList.toggle("loading", loading);
      button.setAttribute("aria-busy", String(loading));
      button.disabled = directSubmit() && (nativeButton.disabled ||
        nativeButton.getAttribute("aria-disabled") === "true");
      const spinner = this.querySelector(".loading-overlay__spinner");
      if (spinner) spinner.classList.toggle("hidden", !loading);
    };
    const targets = owner.dataset.bpCorrectorExperience === "true" || owner.dataset.bpBvsSequential === "true"
      ? [nativeButton]
      : [...new Set([
          nativeButton,
          owner.querySelector(".wt-product__name"),
          owner.querySelector(".wt-bundle"),
          document.querySelector(".wt-footer"),
        ].filter(Boolean))];
    const isInViewport = (element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 &&
        rect.bottom > 0 && rect.top < window.innerHeight;
    };
    const intersected = new Set(targets.filter(isInViewport));
    const updateVisibility = () => {
      if (this._atelierState !== state) return;
      const visible = this.isConnected && intersected.size === 0;
      this.classList.toggle(this.activeClass, visible);
      this.setAttribute("aria-hidden", String(!visible));
      this.inert = !visible;
    };

    state.onClick = (event) => {
      if (directSubmit()) {
        // Leave exactly one native form submission to the browser. Never call
        // requestSubmit/click again and never issue a second cart request.
        if (nativeButton.disabled ||
          nativeButton.getAttribute("aria-disabled") === "true" ||
          nativeButton.classList.contains("loading")) event.preventDefault();
        return;
      }
      event.preventDefault();
      const picker = owner.querySelector(
        `variant-options[data-section="${CSS.escape(sectionId)}"]`,
      );
      const target = picker || form;
      const selectedInput = picker?.querySelector('input[type="radio"]:checked:not(:disabled)') ||
        picker?.querySelector('input[type="radio"]:not(:disabled)');
      // Native radios are visually transparent; their existing tabindex=0
      // labels provide the visible focus indicator without another control.
      const label = selectedInput?.id && picker.querySelector(
        `label[for="${CSS.escape(selectedInput.id)}"]`,
      );
      const focusTarget = label?.tabIndex >= 0 ? label : selectedInput || nativeButton;
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      target.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "center" });
      focusTarget.focus({ preventScroll: true });
    };
    button.addEventListener("click", state.onClick);
    state.onChange = syncButton;
    owner.addEventListener("change", state.onChange);
    state.controlsObserver = new MutationObserver(syncButton);
    state.controlsObserver.observe(nativeButton, {
      attributes: true,
      attributeFilter: ["disabled", "aria-disabled", "class"],
    });
    state.observer = new IntersectionObserver((entries) => {
      if (this._atelierState !== state) return;
      entries.forEach(({ isIntersecting, target }) => {
        if (isIntersecting) intersected.add(target);
        else intersected.delete(target);
      });
      updateVisibility();
    });
    targets.forEach((target) => state.observer.observe(target));
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      [PUB_SUB_EVENTS.cartUpdate, PUB_SUB_EVENTS.cartError].filter(Boolean).forEach((topic) => {
        state.unsubscribers.push(subscribe(topic, syncButton));
      });
    }
    syncButton();
    updateVisibility();
  }

  cleanupAtelier() {
    const state = this._atelierState;
    this._atelierState = null;
    if (state) {
      state.button.removeEventListener("click", state.onClick);
      state.owner.removeEventListener("change", state.onChange);
      state.observer.disconnect();
      state.controlsObserver.disconnect();
      state.unsubscribers.forEach((unsubscribe) => unsubscribe());
    }
    this.classList.remove(this.activeClass);
    this.setAttribute("aria-hidden", "true");
    this.inert = true;
  }

  _overlapsBundlePanel() {
    if (!this._bundleEl || window.innerWidth > 899) return false;
    const rect = this._bundleEl.getBoundingClientRect();
    const topEntered = rect.top <= window.innerHeight;
    const bottomClearedFold = window.innerHeight - rect.bottom >= 100;
    return topEntered && !bottomClearedFold;
  }

  disconnectedCallback() {
    if (this._atelierLifecycle) {
      this.cleanupAtelier();
      document.removeEventListener("shopify:section:load", this._atelierLifecycle);
      document.removeEventListener("shopify:section:unload", this._atelierLifecycle);
      this._atelierLifecycle = null;
      return;
    }
    if (this._onScroll) window.removeEventListener("scroll", this._onScroll);
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}

;(() => {
  const instanceKey = "__bpMascaraPurchaseClearanceV2";
  if (document[instanceKey]) return;
  document[instanceKey] = true;

  const sectionSelector = 'section[data-bp-bvs-sequential="true"][data-product-handle="mascara-volumizzante-incurvante"]';
  const nativeSelector = 'form[data-type="add-to-cart-form"] [name="add"][type="submit"]';
  const heightProperty = "--bp-mascara-sticky-height";
  const hideClass = "bp-mascara-native-wa-clearance";
  let active = null;

  const clearHide = () => document.documentElement.classList.remove(hideClass);
  const cleanup = () => {
    if (!active) {
      clearHide();
      return;
    }
    active.resizeObserver?.disconnect();
    active.visibilityObserver?.disconnect();
    active.stickyObserver?.disconnect();
    active.widgetDiscovery?.disconnect();
    window.removeEventListener("resize", active.onResize);
    if (active.scrollListening) window.removeEventListener("scroll", active.onScroll);
    if (active.frame) cancelAnimationFrame(active.frame);
    active = null;
    document.documentElement.style.removeProperty(heightProperty);
    clearHide();
  };

  const initialize = () => {
    const section = document.querySelector(sectionSelector);
    const sticky = section?.querySelector("sticky-buy-button");
    if (!section || !sticky) {
      cleanup();
      return;
    }
    if (active?.sticky === sticky) return;
    cleanup();

    const state = {
      section, sticky, nativeButton: section.querySelector(nativeSelector), widget: null,
      resizeObserver: null, visibilityObserver: null, stickyObserver: null,
      widgetDiscovery: null, scrollListening: false, frame: 0, onResize: null, onScroll: null,
    };
    active = state;
    const visibleInViewport = (rect) => rect.width > 0 && rect.height > 0 &&
      rect.right > 0 && rect.left < window.innerWidth && rect.bottom > 0 && rect.top < window.innerHeight;
    const intersects = (a, b) => Math.min(a.right, b.right) > Math.max(a.left, b.left) &&
      Math.min(a.bottom, b.bottom) > Math.max(a.top, b.top);
    const measure = () => {
      if (active !== state) return;
      if (!section.isConnected || !sticky.isConnected) {
        cleanup();
        return;
      }
      const height = Math.ceil(sticky.getBoundingClientRect().height);
      if (height > 0) document.documentElement.style.setProperty(heightProperty, `${height}px`);
      const button = state.nativeButton;
      const widget = state.widget;
      const buttonRect = button?.isConnected ? button.getBoundingClientRect() : null;
      const widgetRect = widget?.isConnected ? widget.getBoundingClientRect() : null;
      const collision = button?.isConnected && widget?.isConnected &&
        !sticky.classList.contains("wt-product__sticky-buy--show") &&
        visibleInViewport(buttonRect) && visibleInViewport(widgetRect) &&
        intersects(buttonRect, widgetRect);
      document.documentElement.classList.toggle(hideClass, Boolean(collision));
    };
    const schedule = () => {
      if (state.frame) return;
      state.frame = requestAnimationFrame(() => {
        state.frame = 0;
        measure();
      });
    };
    state.onResize = schedule;
    state.onScroll = () => {
      if (state.nativeButton?.isConnected &&
          visibleInViewport(state.nativeButton.getBoundingClientRect())) schedule();
      else clearHide();
    };
    window.addEventListener("resize", state.onResize, { passive: true });

    if (typeof ResizeObserver === "function") {
      state.resizeObserver = new ResizeObserver(measure);
      state.resizeObserver.observe(sticky);
      if (state.nativeButton) state.resizeObserver.observe(state.nativeButton);
    }
    state.stickyObserver = new MutationObserver(measure);
    state.stickyObserver.observe(sticky, { attributes: true, attributeFilter: ["class"] });

    if (state.nativeButton && typeof IntersectionObserver === "function") {
      state.visibilityObserver = new IntersectionObserver(([entry]) => {
        if (active !== state) return;
        const shouldListen = Boolean(entry?.isIntersecting);
        if (shouldListen !== state.scrollListening) {
          state.scrollListening = shouldListen;
          if (shouldListen) window.addEventListener("scroll", state.onScroll, { passive: true });
          else window.removeEventListener("scroll", state.onScroll);
        }
        measure();
      }, { threshold: 0 });
      state.visibilityObserver.observe(state.nativeButton);
    } else if (state.nativeButton) {
      state.scrollListening = true;
      window.addEventListener("scroll", state.onScroll, { passive: true });
    }

    const attachWidget = () => {
      const widget = document.querySelector("#wa-chat-btn-root");
      if (!widget || active !== state) return false;
      state.widget = widget;
      state.widgetDiscovery?.disconnect();
      state.widgetDiscovery = null;
      state.resizeObserver?.observe(widget);
      measure();
      return true;
    };
    if (!attachWidget()) {
      state.widgetDiscovery = new MutationObserver(attachWidget);
      state.widgetDiscovery.observe(document.documentElement, { childList: true, subtree: true });
    }
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (active === state) measure();
      }).catch(() => {});
    }
    measure();
  };

  document.addEventListener("shopify:section:unload", (event) => {
    if (active && event.target instanceof Element && event.target.contains(active.section)) cleanup();
  });
  document.addEventListener("shopify:section:load", (event) => {
    if (event.target instanceof Element &&
        (event.target.matches(sectionSelector) || event.target.querySelector(sectionSelector))) initialize();
  });
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
